import { useQuery } from '@tanstack/react-query'
import { Spinner } from '@/components/ui/spinner'

export enum QUERY_KEYS {
  BASE64_IMAGE = 'base64Image',
}

const getBase64FromUrl = async (url): Promise<string> => {
  const imageData = await fetch(url)
  if (!imageData.ok) {
    throw new Error(`${imageData.statusText}`)
  }
  const blob = await imageData.blob()
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.readAsDataURL(blob)
    reader.onloadend = () => {
      const base64data = reader.result as string
      resolve(base64data)
    }
  })
}

const useBase64Image = (url?: string) => {
  const isEnabled = !!url

  const queryKey = isEnabled
    ? (() => {
        try {
          const { host, pathname } = new URL(url)
          return [QUERY_KEYS.BASE64_IMAGE, `${host}${pathname}`]
        } catch {
          return [QUERY_KEYS.BASE64_IMAGE, 'invalid-url']
        }
      })()
    : [QUERY_KEYS.BASE64_IMAGE, 'no-url']

  return useQuery({
    retry: 3,
    retryDelay: 100,
    queryKey,
    queryFn: () => getBase64FromUrl(url!),
    throwOnError: false,
    enabled: isEnabled,
  })
}

interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  className?: string
  src: string
}

export const Image = ({ className, src, ...props }: ImageProps) => {
  const {
    data: base64Image,
    isLoading,
    isFetching,
    isError,
    error,
  } = useBase64Image(src)

  if (isError) {
    console.error(error.name, error.message)
  }

  if (isLoading || isFetching || !base64Image) {
    return <Spinner />
  }

  return <img className={className} src={base64Image} alt="Image" {...props} />
}
